import React, { useState } from 'react';
import ReportHelper from '../../helpers/ReportHelper';
import { InterpretationScoreBar } from '../InterpretationScoreBar/InterpretationScoreBar';
import CompetencyIcon from '../CompetencyProfileExtended/components/CompetencyIcon/CompetencyIcon';
import EditableText from '../../../EditableText/EditableText';
import classNames from 'classnames';
import Utils from '../../../../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CompetencyTag from './components/CompetencyTag/CompetencyTag';
import style from './style/interpretationcompetency.scss';

export const InterpretationCompetency = ({
                                             competency,
                                             languageId,
                                             i18n,
                                             saveCompetency,
                                             saveInterval,
                                             openInterpretationCompetenciesModal,
                                             showHeader = true,
                                             initials
                                         }) => {
    const [competencyScore, setCompetencyScore] = useState(competency.score || competency.autoCalculatedScore || null);
    const [competencyComment, setCompetencyComment] = useState(competency.comment || '');
    const [competencyLabel, setCompetencyLabel] = useState(competency.label || '');
    const [isScoreEdited, setIsScoreEdited] = useState(!!competency.score);

    const allowEditing = !!saveCompetency;
    const hasModal = !!openInterpretationCompetenciesModal;

    const handleSaveCompetencyComment = payload => {
        if (!allowEditing) {
            return null;
        }

        return saveCompetency({
            value: competencyScore,
            competencyId: competency.id,
            comment: payload.value
        }).then(savedCompetency => {
            setCompetencyComment(savedCompetency.comment);
        });
    };

    const handleSaveCompetencyScore = score => {
        if (!allowEditing) {
            return null;
        }

        return saveCompetency({
            value: score,
            competencyId: competency.id,
            comment: competencyComment
        }, true).then(savedCompetency => {
            setCompetencyScore(savedCompetency.value);
            setCompetencyLabel(savedCompetency.label);
            setIsScoreEdited(true);
        });
    };

    const createValidSelector = label => label
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '') // remove special characters
        .trim()
        .split(/\s+/)
        .join('_');

    const consultantCommentSection = competency => {
        if (!competency.comment) {
            return null;
        }

        return <section className={style.staticCompetencyComment}>
            {i18n.report_section_interpretation_competency_comment_by_consultant}
            <span dangerouslySetInnerHTML={{ __html: competency.comment }} />
        </section>;
    };

    const getHyphenatedCompetencyName = () => Utils.hyphenateText(ReportHelper.getCompetencyName(competency, languageId, i18n), languageId);

    const getCompetencyTag = () => {
        if (competency.competencyAliases[languageId]?.id) {
            return <CompetencyTag label={i18n.report_section_interpretation_competency_tag_alias} classes={{ root: `${style.competencyTag} ${style.aliasTag}` }} />;
        }

        return competency.isCustom
            ? <CompetencyTag label={i18n.report_section_interpretation_competency_tag_custom} classes={{ root: `${style.competencyTag} ${style.customTag}` }} />
            : <CompetencyTag label={i18n.report_section_interpretation_competency_tag_ltp} classes={{ root: `${style.competencyTag} ${style.ltpTag}` }} />;
    };

    const getScoreBarTag = () => {
        if (!competency.score && !competency.autoCalculatedScore) {
            return <CompetencyTag
                label={<><FontAwesomeIcon icon={'exclamation-triangle'} />{i18n.report_section_interpretation_competency_tag_empty}</>}
                classes={{ root: style.emptyTag }} />;
        }

        return isScoreEdited
            ? <CompetencyTag label={i18n.report_section_interpretation_competency_tag_consultant} classes={{ root: style.consultantTag }} />
            : <CompetencyTag label={i18n.report_section_interpretation_competency_tag_online} classes={{ root: style.onlineTag }} />;
    };

    return <li key={competency.handle}
               className={`${style.interpretationCompetency} ${hasModal ? style.interpretationCompetency__hover : ''}`}>
        <main onClick={hasModal ? () => openInterpretationCompetenciesModal(competency) : null}>
            {showHeader && <header className={style.titleSection}>
                <CompetencyIcon handle={`icon_${competency.translationKeyName}`}/>
                <div className={style.nameAndDefinition}>
                    {allowEditing && getCompetencyTag()}
                    <h4 dangerouslySetInnerHTML={{ __html: getHyphenatedCompetencyName() }}/>
                    <p>{ReportHelper.getCompetencyDefinition(competency, languageId, i18n)}</p>
                </div>
            </header>}
            <section className={`${style.scoreBarWithLabel} ${showHeader ? '' : style.scoreBarWithLabel__extended}`}>
                <div className={classNames(style.scoreBar, { [style.preview]: !allowEditing })}>
                    <InterpretationScoreBar score={competencyScore} saveCompetencyScore={handleSaveCompetencyScore} initials={initials}/>
                    {allowEditing && getScoreBarTag()}
                </div>
                <span>{competencyLabel}</span>
            </section>
        </main>
        <aside className={showHeader ? '' : style.extendedComment}>
            { allowEditing
                ? <EditableText
                    saveReportText={handleSaveCompetencyComment}
                    isFinalized={false}
                    textEditable={true}
                    allowedToEditReflection={allowEditing}
                    templateSlug={competency.handle}
                    slug={competency.handle}
                    name={competency.handle}
                    text={competency.comment}
                    saveInterval={saveInterval}
                    textareaPlaceholderText={i18n.report_section_interpretation_competency_comment_placeholder}
                    id={createValidSelector(competency.handle)}
                />
                : consultantCommentSection(competency)
            }
        </aside>
    </li>;
};
